import {Document, Filter, FindOptions} from 'mongodb'
import useSWR, {SWRConfiguration} from 'swr'

import objectToQueryParams from 'lib/utils/object-to-query-params'
import {ResponseError} from 'lib/utils/safe-fetch'

function createQueryUrl(baseUrl: string, objects = {}) {
  const parts = [baseUrl]
  const queryParams = objectToQueryParams(objects)
  if (queryParams?.length > 0) parts.push(queryParams)
  return parts.join('?')
}

export type UseOneOptions<T extends Document = Document> = {
  findOptions?: FindOptions<T>
  swrConfig?: SWRConfiguration<T, ResponseError>
}

/**
 * Hook to fetch and use one value.
 */
export function createUseOne<T extends CL.IModel>(
  baseUrl: string,
  stableOpts?: UseOneOptions<T>
) {
  // When using a model directly, there's a good chance we are editing it.
  // Revalidating on focus could overwrite local changes.
  const useOneBaseConfig: SWRConfiguration<T, ResponseError> = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  }
  const finalConfig = {
    ...useOneBaseConfig,
    ...(stableOpts?.swrConfig ?? {})
  }

  return function useOne(_id?: string) {
    const url = createQueryUrl(`${baseUrl}/${_id}`, {
      options: stableOpts?.findOptions
    })
    const idIsValid = typeof _id === 'string' && _id.length > 0
    const key = idIsValid ? url : null
    return useSWR<T, ResponseError>(key, finalConfig)
  }
}

export type UseManyOptions<T extends Document = Document> = {
  findOptions?: FindOptions<T>
  swrConfig?: SWRConfiguration<T[], ResponseError>
}

/**
 * Hook to fetch and use a list of values from this collection.
 */
export function createUseMany<T extends CL.IModel>(
  baseUrl: string,
  stableOpts?: UseManyOptions<T>
) {
  return function useMany(query?: Filter<T>, opts?: UseManyOptions<T>) {
    const config = opts?.swrConfig ?? stableOpts?.swrConfig
    const options = opts?.findOptions ?? stableOpts?.findOptions

    const url = createQueryUrl(baseUrl, {query, options})
    const key = query != null ? url : null
    return useSWR<T[], ResponseError>(key, config)
  }
}
